/**
 * Shared visualization utilities for debug tools
 * Provides reusable functions for element highlighting and visualization
 */

declare const THREE: any;

export type ColorName = 'red' | 'green' | 'blue' | 'yellow' | 'purple' | 'cyan' | 'orange' | 'pink';

export interface ElementToVisualize {
  dbId: number;
  color: ColorName;
}

export interface VisualizationOptions {
  isolate?: boolean;
  fitToView?: boolean;
  showAll?: boolean;
}

// Get color mapping dynamically to ensure THREE is available
export function getColorVector(color: ColorName): any {
  if (typeof THREE === 'undefined') {
    console.error('[Visualization] THREE.js not available');
    return null;
  }
  
  const colorVectors: Record<ColorName, any> = {
    'red': new THREE.Vector4(1, 0, 0, 0.6),
    'green': new THREE.Vector4(0, 1, 0, 0.6),
    'blue': new THREE.Vector4(0, 0, 1, 0.6),
    'yellow': new THREE.Vector4(1, 1, 0, 0.6),
    'purple': new THREE.Vector4(0.5, 0, 1, 0.6),
    'cyan': new THREE.Vector4(0, 1, 1, 0.6),
    'orange': new THREE.Vector4(1, 0.5, 0, 0.6),
    'pink': new THREE.Vector4(1, 0.5, 0.8, 0.6)
  };
  
  return colorVectors[color] || colorVectors['blue'];
}

// Hex colors for UI display
export const HEX_COLORS: Record<ColorName, string> = {
  'red': '#FF0000',
  'green': '#00FF00',
  'blue': '#0000FF',
  'yellow': '#FFFF00',
  'purple': '#8000FF',
  'cyan': '#00FFFF',
  'orange': '#FF8000',
  'pink': '#FF80CC'
};

/**
 * Visualize multiple elements with different colors
 */
export async function visualizeElements(
  viewer: any,
  elements: ElementToVisualize[],
  options: VisualizationOptions = {}
): Promise<void> {
  if (!viewer || !elements.length) {
    console.warn('[Visualization] No viewer or elements provided');
    return;
  }

  console.log('[Visualization] Visualizing elements:', elements);

  // Clear previous highlights
  viewer.clearThemingColors();
  
  // Handle isolation/showing
  if (options.showAll || !options.isolate) {
    viewer.isolate([]);
    viewer.showAll();
  }
  
  // Apply colors to each element
  elements.forEach(({ dbId, color }) => {
    const colorVector = getColorVector(color);
    if (colorVector) {
      viewer.setThemingColor(dbId, colorVector);
      console.log(`[Visualization] Applied ${color} to element ${dbId}`);
    } else {
      console.error(`[Visualization] Could not get color vector for ${color}`);
    }
  });
  
  // Optionally isolate the colored elements
  if (options.isolate) {
    const dbIds = elements.map(e => e.dbId);
    viewer.isolate(dbIds);
  }
  
  // Fit camera to show all highlighted elements
  if (options.fitToView) {
    const dbIds = elements.map(e => e.dbId);
    viewer.fitToView(dbIds, viewer.model, true);
  }
  
  // Force viewer update
  viewer.impl.invalidate(true, true, true);
}

/**
 * Clear all visualizations
 */
export function clearVisualization(viewer: any): void {
  if (!viewer) return;

  console.log('[Visualization] Clearing all visualizations');

  // Reset any fragment-material transparency we applied (e.g. ceiling ghost).
  // Imported lazily to avoid a cyclic dep with ViewerService.
  import('@/services/core/viewerTools')
    .then(({ ViewerService }) => ViewerService.resetTransparentFragments(viewer))
    .catch(() => { /* best-effort */ });

  viewer.clearThemingColors();
  viewer.isolate([]);
  viewer.showAll();
  viewer.impl.invalidate(true, true, true);
}

/**
 * Highlight a single element
 */
export async function highlightElement(
  viewer: any,
  dbId: number,
  color: ColorName = 'blue'
): Promise<void> {
  return visualizeElements(viewer, [{ dbId, color }]);
}

/**
 * Create a visual bounding box overlay for debugging
 */
export function visualizeBoundingBox(
  viewer: any,
  box: any,
  color: string = '#00FF00',
  opacity: number = 0.3
): void {
  if (!viewer || !box) return;
  
  const geometry = new THREE.BoxGeometry(
    box.max.x - box.min.x,
    box.max.y - box.min.y,
    box.max.z - box.min.z
  );
  
  const material = new THREE.MeshBasicMaterial({
    color: color,
    opacity: opacity,
    transparent: true,
    wireframe: true
  });
  
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(
    (box.min.x + box.max.x) / 2,
    (box.min.y + box.max.y) / 2,
    (box.min.z + box.max.z) / 2
  );
  
  // Add to viewer overlay
  if (!viewer.impl.overlayScenes['boundingBox']) {
    viewer.impl.createOverlayScene('boundingBox');
  }
  
  viewer.impl.addOverlay('boundingBox', mesh);
  viewer.impl.invalidate(true, true, true);
  
  console.log('[Visualization] Added bounding box overlay');
}

/**
 * Clear bounding box overlays
 */
export function clearBoundingBoxOverlay(viewer: any): void {
  if (!viewer) return;
  
  if (viewer.impl.overlayScenes['boundingBox']) {
    viewer.impl.clearOverlay('boundingBox');
    viewer.impl.invalidate(true, true, true);
    console.log('[Visualization] Cleared bounding box overlay');
  }
}